import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EmployeeReportFilter } from 'src/app/shared/personnel-files/models/EmployeeReportFilter';
import { DevelopmentObjective } from '../../development-objectives/models/DevelopmentObjective';
import { Student } from '../../lms/learningpaths/models/interfaces';
import { ConversationStatus } from '../enums/ConversationStatus';
import { ConversationTemplateRole } from '../enums/ConversationTemplateRole';
import { ConversationAccess } from '../models/ConversationAccess';
import { ConversationTemplateVersionSectionItem } from '../models/ConversationTemplateVersionSectionItem';
import { CurrentConversation } from '../models/CurrentConversation';
import { CurrentConversationDeadline } from '../models/CurrentConversationDeadline';
import { CurrentConversationOverviewFeedback } from '../models/CurrentConversationOverviewFeedback';
import { CurrentConversationSectionItemContentFeedback } from '../models/CurrentConversationSectionItemContentFeedback';
import { CurrentConversationSectionItemFeedback } from '../models/CurrentConversationSectionItemFeedback';
import { CurrentConversationTodo } from '../models/CurrentConversationTodo';
import { OngoingConversationTemplateIdsPerEmployee } from '../models/OngoingConversationTemplateIdsPerEmployee';
import { Peer } from '../models/Peer';
import { ConversationSetupType } from '../enums/ConversationSetupType';
import { ConversationAttachment } from '../models/ConversationAttachment';
import { ConversationSignee } from '../models/ConversationSignee';
import { CurrentConversationSectionItemChosenItem } from '../models/CurrentConversationSectionItemChosenItem';
import { CurrentConversationSectionItemContent } from '../models/CurrentConversationSectionItemContent';
import { ConversationPrivateParticipant } from '../models/ConversationPrivateParticipant';

@Injectable({
  providedIn: 'root'
})
export class ConversationService {

  public resized: Subject<void> = new Subject();

  constructor(private http: HttpClient) { }

  // #region general
  public getStatusClass(status: ConversationStatus) {
    switch (status) {
      case ConversationStatus.NotStarted: return '#bfbfbf';
      case ConversationStatus.Started: return '#EE7622';
      case ConversationStatus.ToBeSigned: return '#EE7622';
      case ConversationStatus.Completed: return '#28A745';
      case ConversationStatus.Deleted: return 'red';
      default: return '#28A745'
    }
  }

  public getStatusName(status: ConversationStatus) {
    switch (status) {
      case ConversationStatus.NotStarted: return 'not_started';
      case ConversationStatus.Started: return 'started';
      case ConversationStatus.ToBeSigned: return 'to_be_signed';
      case ConversationStatus.Completed: return 'completed';
      case ConversationStatus.Deleted: return 'deleted';
      default: return 'started'
    }
  }
  // #endregion general


  // #region LOGIC
  public getCurrentConversationRole(isPeer = false, isOtherEmployee = false): ConversationTemplateRole {
    // WATERFALL SYSTEM
    if (localStorage.getItem('corporatehr_view') === '1') {
      return ConversationTemplateRole.CorporateHR;
    }

    if (localStorage.getItem('localhr_view') === '1') {
      return ConversationTemplateRole.LocalHR;
    }

    if (localStorage.getItem('delegate_view') === '1') {
      return ConversationTemplateRole.Delegate;
    }

    if (localStorage.getItem('matrixmanager_view') === '1' || localStorage.getItem('convers_matrixmanager_view') === '1') {
      return ConversationTemplateRole.Matrix;
    }

    if (localStorage.getItem('manager_view') === '1' && localStorage.getItem('convers_view_n2') === '1') {
      return ConversationTemplateRole.N2;
    }

    if (localStorage.getItem('manager_view') === '1') {
      return ConversationTemplateRole.Manager;
    }

    if (localStorage.getItem('convers_view_peer') === '1' || isPeer) {
      return ConversationTemplateRole.Peer;
    }

    if (isOtherEmployee) {
      return ConversationTemplateRole.OtherEmployee;
    }

    return ConversationTemplateRole.Employee;
  }

  public setCurrentConversationRole(role: ConversationTemplateRole): void {
    localStorage.removeItem('convers_matrixmanager_view');
    localStorage.removeItem('convers_view_n2');
    localStorage.removeItem('convers_view_peer');

    switch (role) {
      case ConversationTemplateRole.CorporateHR:
        localStorage.setItem('corporatehr_view', '1');
        break;
      case ConversationTemplateRole.LocalHR:
        localStorage.setItem('localhr_view', '1');
        break;
      case ConversationTemplateRole.Delegate:
        localStorage.setItem('delegate_view', '1');
        break;
      case ConversationTemplateRole.Matrix:
        localStorage.setItem('convers_matrixmanager_view', '1');
        break;
      case ConversationTemplateRole.Manager:
        localStorage.setItem('manager_view', '1');
        break;
      case ConversationTemplateRole.N2:
        localStorage.setItem('convers_view_n2', '1');
        break;
      case ConversationTemplateRole.Peer:
        localStorage.setItem('convers_view_peer', '1');
        break;
    }
  }

  public getRoleName(role: ConversationTemplateRole) {
    switch (role) {
      case ConversationTemplateRole.Employee: return "employeecapital";
      case ConversationTemplateRole.Peer: return "peer_capital";
      case ConversationTemplateRole.Manager: return "manager_capital";
      case ConversationTemplateRole.Delegate: return "delegated_manager_capital";
      case ConversationTemplateRole.Matrix: return "matrix_manager_capital";
      case ConversationTemplateRole.N2: return "n2_capital";
      case ConversationTemplateRole.LocalHR: return "localhr_capital";
      case ConversationTemplateRole.CorporateHR: return "corporatehr_capital";
      case ConversationTemplateRole.OtherEmployee: return "another_employee";
      default: return "";
    }
  }

  public getRoleUserLevel(role: ConversationTemplateRole) {
    switch (role) {
      case ConversationTemplateRole.CorporateHR: return 1;
      case ConversationTemplateRole.LocalHR: return 2;
      case ConversationTemplateRole.Employee:
      case ConversationTemplateRole.Peer:
      case ConversationTemplateRole.Manager:
      case ConversationTemplateRole.Delegate:
      case ConversationTemplateRole.Matrix:
      case ConversationTemplateRole.N2:
        return 3;
      case ConversationTemplateRole.OtherEmployee: return 6;
      default: return 3;
    }
  }

  public useTextboxGeneral(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesTextboxGeneral;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesTextboxGeneral;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesTextboxGeneral;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesTextboxGeneral;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesTextboxGeneral;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesTextboxGeneral;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesTextboxGeneral;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesTextboxGeneral;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesTextboxGeneral;
    }
  }

  public textboxGeneralMandatory(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesTextboxGeneralMandatory;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesTextboxGeneralMandatory;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesTextboxGeneralMandatory;
    }
  }

  public useTextboxPerItem(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesTextboxPerItem;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesTextboxPerItem;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesTextboxPerItem;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesTextboxPerItem;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesTextboxPerItem;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesTextboxPerItem;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesTextboxPerItem;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesTextboxPerItem;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesTextboxPerItem;
    }
  }

  public textboxPerItemMandatory(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesTextboxPerItemMandatory;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesTextboxPerItemMandatory;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesTextboxPerItemMandatory;
    }
  }

  public useScorePerItem(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesScorePerItem;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesScorePerItem;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesScorePerItem;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesScorePerItem;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesScorePerItem;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesScorePerItem;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesScorePerItem;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesScorePerItem;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesScorePerItem;
    }
  }

  public scorePerItemMandatory(role: ConversationTemplateRole, templateConversationSectionItem: ConversationTemplateVersionSectionItem) {
    switch (role) {
      case ConversationTemplateRole.Employee: return templateConversationSectionItem.employeeUsesScorePerItemMandatory;
      case ConversationTemplateRole.Peer: return templateConversationSectionItem.peerUsesScorePerItemMandatory;
      case ConversationTemplateRole.Manager: return templateConversationSectionItem.managerUsesScorePerItemMandatory;
      case ConversationTemplateRole.Delegate: return templateConversationSectionItem.delegateUsesScorePerItemMandatory;
      case ConversationTemplateRole.Matrix: return templateConversationSectionItem.matrixUsesScorePerItemMandatory;
      case ConversationTemplateRole.N2: return templateConversationSectionItem.n2UsesScorePerItemMandatory;
      case ConversationTemplateRole.LocalHR: return templateConversationSectionItem.localHrUsesScorePerItemMandatory;
      case ConversationTemplateRole.CorporateHR: return templateConversationSectionItem.corporateHrUsesScorePerItemMandatory;
      case ConversationTemplateRole.OtherEmployee: return templateConversationSectionItem.otherEmployeeUsesScorePerItemMandatory;
    }
  }

  public resizeFrames() {
    this.resized.next();
  }
  // #endregion LOGIC

  // #region CONVERSATION
  public getConversation(conversationId: number, role: ConversationTemplateRole): Observable<CurrentConversation> {
    return this.http.post<CurrentConversation>('/api/conversation/' + conversationId + '/' + role, null);
  }

  public createConversation(templateId: number, students: Student[], currentUserRole: ConversationTemplateRole, customTitle?: string): Observable<ConversationAccess> {
    return this.http.post<ConversationAccess>('/api/conversation/create', { templateId, students, currentUserRole, customTitle });
  }

  public getConversationParticipants(studentIds: number[], conversationTemplateVersionId: number): Observable<ConversationPrivateParticipant[]> {
    return this.http.post<ConversationPrivateParticipant[]>('/api/conversation/participants/' + conversationTemplateVersionId, studentIds);
  }

  public optOutPeers(conversationId: number, usePeersOptOut: boolean, todo: CurrentConversationTodo, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/optoutpeers/' + conversationId + "?role=" + role, { usePeersOptOut, todo });
  }

  public getPeers(conversationId: number): Observable<Peer[]> {
    return this.http.post<Peer[]>('/api/conversation/getpeers/' + conversationId, null);
  }

  public selectPeers(conversationId: number, peersSelected: Peer[], role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/selectpeers/' + conversationId + "?role=" + role, peersSelected);
  }

  public validatePeers(conversationId: number, peersSelected: Peer[], role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/validatepeers/' + conversationId + "?role=" + role, peersSelected);
  }


  public selectItems(conversationId: number, conversationTemplateVersionSectionItemId: number, itemsSelected: CurrentConversationSectionItemChosenItem[], role: ConversationTemplateRole, conversationSetupType: ConversationSetupType): Observable<void> {
    return this.http.post<void>('/api/conversation/selectitems/' + conversationId + '/' + conversationTemplateVersionSectionItemId + "?role=" + role + "&conversationsetuptype=" + conversationSetupType, itemsSelected);
  }

  public validateItems(conversationId: number, conversationTemplateVersionSectionItemId: number, itemsSelected: CurrentConversationSectionItemChosenItem[], role: ConversationTemplateRole, conversationSetupType: ConversationSetupType): Observable<void> {
    return this.http.post<void>('/api/conversation/validateitems/' + conversationId + '/' + conversationTemplateVersionSectionItemId + "?role=" + role + "&conversationsetuptype=" + conversationSetupType, itemsSelected);
  }

  public addNewChosenItem(conversationId: number, conversationTemplateVersionSectionItemId: number, chosenItem: CurrentConversationSectionItemChosenItem, role: ConversationTemplateRole, conversationSetupType: ConversationSetupType): Observable<CurrentConversationSectionItemChosenItem> {
    return this.http.post<CurrentConversationSectionItemChosenItem>('/api/conversation/addnewchosenitem/' + conversationId + '/' + conversationTemplateVersionSectionItemId + "?role=" + role + "&conversationsetuptype=" + conversationSetupType, chosenItem);
  }

  public updateFaceToFace(conversationId: number, faceToFaceObject: any, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/updatefacetoface/' + conversationId + "?role=" + role, faceToFaceObject);
  }

  public sendReminder(conversationId: number, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/sendreminder/' + conversationId + "?role=" + role, null);
  }

  public deleteConversation(conversationId: number, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/delete/' + conversationId + "?role=" + role, null);
  }

  public giveGoToComplete(conversationId: number, giveGoObject: any, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/givego/' + conversationId + "?role=" + role, giveGoObject);
  }

  public completeConversation(conversationId: number, completingObject: any, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/complete/' + conversationId + "?role=" + role, completingObject);
  }

  public updateDeadlines(conversationId: number, deadlinesToUpdate: CurrentConversationDeadline[], role: ConversationTemplateRole) {
    return this.http.post<void>('/api/conversation/updatedeadlines/' + conversationId + "?role=" + role, deadlinesToUpdate);
  }

  public saveOverviewFeedback(conversationId: number, feedback: CurrentConversationOverviewFeedback): Observable<CurrentConversationOverviewFeedback> {
    return this.http.post<CurrentConversationOverviewFeedback>('/api/conversation/' + conversationId + '/saveoverviewfeedback', feedback);
  }

  public saveCustomScore(conversationId: number, score: number): Observable<void> {
    return this.http.post<void>('/api/conversation/updatecustomtotalscore/' + conversationId, score);
  }

  public getOngoingConversationTemplateIdsForEmployees(studentIds: number[]): Observable<OngoingConversationTemplateIdsPerEmployee[]> {
    return this.http.post<OngoingConversationTemplateIdsPerEmployee[]>('/api/conversation/getongoingconversationtemplateidsforemployees', studentIds);
  }

  public getConversationSignees(conversationId: number): Observable<ConversationSignee[]> {
    return this.http.post<ConversationSignee[]>('/api/conversation/signees/' + conversationId, null);
  }

  public getAttachments(conversationId: number): Observable<ConversationAttachment[]> {
    return this.http.post<ConversationAttachment[]>('/api/conversation/getattachments/' + conversationId, null);
  }

  public addAttachment(file: any, conversationId: number, role: ConversationTemplateRole): Observable<void> {
    let formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>('/api/conversation/addattachment/' + conversationId + "?role=" + role, formData);
  }

  public addLibraryAttachments(attachments: ConversationAttachment[], conversationId: number, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/addlibraryattachments/' + conversationId + "?role=" + role, attachments);
  }

  public downloadAttachment(attachmentId: number): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>('/api/conversation/downloadattachment/' + attachmentId, { observe: 'response', responseType: 'blob' as 'json' });
  }

  public deleteAttachment(attachmentId: number, conversationId: number, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/deleteattachment/' + attachmentId + '/' + conversationId + "?role=" + role, null);
  }

  // #endregion CONVERSATION

  // #region TODO
  public completeTodo(conversationId: number, todo: CurrentConversationTodo, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/completetodo/' + conversationId + "?role=" + role, todo);
  }

  public uncompleteTodo(conversationId: number, todo: CurrentConversationTodo, role: ConversationTemplateRole): Observable<void> {
    return this.http.post<void>('/api/conversation/uncompletetodo/' + conversationId + "?role=" + role, todo);
  }
  // #endregion TODO

  // #region CONTENT
  public saveGeneralFeedback(conversationId: number, feedback: CurrentConversationSectionItemFeedback): Observable<CurrentConversationSectionItemFeedback> {
    return this.http.post<CurrentConversationSectionItemFeedback>('/api/conversation/' + conversationId + '/savegeneralfeedback', feedback);
  }

  public saveContentFeedback(conversationId: number, feedback: CurrentConversationSectionItemContentFeedback): Observable<CurrentConversationSectionItemContentFeedback> {
    return this.http.post<CurrentConversationSectionItemContentFeedback>('/api/conversation/' + conversationId + '/savecontentfeedback', feedback);
  }

  public saveContent(conversationId: number, content: CurrentConversationSectionItemContent[], role: ConversationTemplateRole): Observable<CurrentConversationSectionItemContent[]> {
    return this.http.post<CurrentConversationSectionItemContent[]>('/api/conversation/' + conversationId + '/savecontent?role=' + role, content);
  }
  // #endregion CONTENT

  public getEmployeesForOtherEmployee(filter: EmployeeReportFilter, conversationTemplateId: number): Observable<Student[]> {
    return this.http.post<Student[]>('/api/conversation/employeesforotheremployee', { filter: filter, conversationTemplateId: conversationTemplateId });
  }

  public getEmployeesForOtherEmployeeCount(filter: EmployeeReportFilter, conversationTemplateId: number): Observable<number> {
    return this.http.post<number>('/api/conversation/employeesforotheremployee/count', { filter: filter, conversationTemplateId: conversationTemplateId });
  }

  public getPdf(id: number, role: ConversationTemplateRole): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>('/api/conversation/pdf/' + id + '?role=' + role, { observe: 'response', responseType: 'blob' as 'json' });
  }
}
